import React, { useReducer } from "react";
import { Routes, Route, useParams, Outlet } from "react-router-dom";

import { useFoQuoting21Flag } from "@hooks/useFeatureFlag";
import PlanDetailsView from "./PlanDetailsView";
import PlanOptionsView from "./PlanOptionsView";
import QuoteErrorView from "./QuoteErrorView";
import QuoteLandingView from "./QuoteLandingView";
import QuoteReviewView from "./QuoteReviewView";
import QuoteStatusView from "./QuoteStatusView";
import VehicleDetailsView from "./VehicleDetailsView";
import DiscountOptionsView from "./DiscountOptionsView";

import { selectedVehicleReducer } from "./selectedVehicleReducer";
import {
  customerPotentialDetailReducer,
  customerPotentialDetailInitialState,
} from "./customerPotentialDetailReducer";
import { pricingRecordReducer } from "./pricingRecordReducer";
import { filterOptionsReducer } from "./filterOptionReducer";
import { errorInitialState, errorReducer } from "./errorReducer";
import { PlanType } from "@utils/quotePlanOptions";

export default function QuoteBuilderParentView() {
  const { potentialId, country } = useParams();

  // State to manage the flow of quote building
  const [selectedVehicleState, selectedVehicleDispatch] = useReducer(
    selectedVehicleReducer,
    null //selectedVehicleInitialState
  );

  const [pricingRecord, pricingRecordDispatch] = useReducer(
    pricingRecordReducer,
    null
  );

  const [filterOptions, filterOptionsDispatch] = useReducer(
    filterOptionsReducer,
    null
  );

  const [customerDetails, customerPotentialDetailDispatch] = useReducer(
    customerPotentialDetailReducer,
    customerPotentialDetailInitialState
  );

  const [errorState, errorDispatch] = useReducer(
    errorReducer,
    errorInitialState
  );

  // Get the flag to determine if we should be quoting FlexiOwn 2.1 or not(2.0)
  //TODO not currently used - expecting to use it as more 2.1 tickets are implemented
  const [foQuoting21Loading, foQuoting21Error, foQuoting21Enabled] = useFoQuoting21Flag();

  function buildQuoteBuilderPath(targetComponent: string) {
    return `/quotes/${country}/${potentialId}/${targetComponent}`;
  }

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            <QuoteLandingView
              filterDispatch={selectedVehicleDispatch}
              customerPotentialDetailDispatch={customerPotentialDetailDispatch}
              pricingRecordDispatch={pricingRecordDispatch}
              navigateNewQuoteTarget={buildQuoteBuilderPath("find-plan")}
              navigateStatusPageTarget={buildQuoteBuilderPath("status")}
              navigateErrorPageTarget={buildQuoteBuilderPath("error")}
            />
          }
        ></Route>
        <Route
          path="error"
          element={
            <QuoteErrorView
              customerPotentialDetailState={customerDetails}
              pricingRecordState={pricingRecord}
              navigateLandingPageTarget={buildQuoteBuilderPath("")}
            />
          }
        ></Route>
        <Route
          path="find-plan"
          element={
            <PlanDetailsView
              customerPotentialDetailState={customerDetails}
              filterDispatch={selectedVehicleDispatch}
              pricingRecordState={pricingRecord}
              filterOptionsState={filterOptions}
              pricingRecordDispatch={pricingRecordDispatch}
              filterOptionsDispatch={filterOptionsDispatch}
              filterState={selectedVehicleState}
              errorState={errorState}
              errorDispatch={errorDispatch}
              navigateTarget={buildQuoteBuilderPath("vehicle-details")}
              navigateLandingPageTarget={buildQuoteBuilderPath("")}
              foQuoting21Enabled={foQuoting21Enabled}
            />
          }
        ></Route>
        <Route
          path="status"
          element={
            <QuoteStatusView
              customerPotentialDetailState={customerDetails}
              pricingRecordState={pricingRecord}
              pricingRecordDispatch={pricingRecordDispatch}
              navigateLandingPageTarget={buildQuoteBuilderPath("")}
              navigateNewQuoteTarget={buildQuoteBuilderPath("")}
            />
          }
        ></Route>
        <Route
          path="vehicle-details"
          element={
            <VehicleDetailsView
              pricingRecordDispatch={pricingRecordDispatch}
              pricingRecordState={pricingRecord}
              navigateLandingPageTarget={buildQuoteBuilderPath("")}
              navigateNextTarget={
                selectedVehicleState?.selectedPlan === PlanType.FLEXI_OWN
                  ? buildQuoteBuilderPath("plan-options")
                  : buildQuoteBuilderPath("discount-options")
              }
            />
          }
        ></Route>
        <Route
          path="plan-options"
          element={
            <PlanOptionsView
              pricingRecordDispatch={pricingRecordDispatch}
              pricingRecordState={pricingRecord}
              navigateLandingPageTarget={buildQuoteBuilderPath("")}
              navigateNextTarget={buildQuoteBuilderPath("review-quote")}
              foQuoting21Enabled={foQuoting21Enabled}
            />
          }
        ></Route>
        {/* TODO: [FT2.0] Create a separate component specific to discount options
          * This new component should be a next page prop for VehicleDetailsView when the selected plan is 'ft'
         */}
        <Route
          path="discount-options"
          element={
            <DiscountOptionsView
              pricingRecordDispatch={pricingRecordDispatch}
              pricingRecordState={pricingRecord}
              navigateLandingPageTarget={buildQuoteBuilderPath("")}
              navigateNextTarget={buildQuoteBuilderPath("review-quote")}
            />
          }
        ></Route>
        <Route
          path="review-quote"
          element={
            <QuoteReviewView
              customerPotentialDetailState={customerDetails}
              pricingRecordState={pricingRecord}
              pricingRecordDispatch={pricingRecordDispatch}
              navigateLandingPageTarget={buildQuoteBuilderPath("")}
              navigateNextTarget={buildQuoteBuilderPath("status")}
              foQuoting21Enabled={foQuoting21Enabled}
            />
          }
        ></Route>
      </Routes>
    </>
  );
}
